import includes from 'lodash/includes';
import moment from 'moment';
import React, { useRef, useState, useEffect, useCallback } from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import { MultiSelect } from '../../../../widgets-v6/select';
import VirtualizedTable from 'packages/widgets-v6/virtualized-table';
/** import widgets components in the section above, Sort Lines Ascending */

import {
    filterRecordsByKeywords,
    sortRecordsByName,
} from 'states/resources/organizations/business-logic';
import { useOverviewAdData } from '../../hook';
import { Tabs } from '../../modules/tabs';
/** import classes and others in the section above, Sort Lines Ascending */

const useStyles = makeStyles(() => ({
    formControl: {
        width: '100%',
        minWidth: 400,
        maxWidth: 720,
    },
    orgFilterBox: {
        flex: '1 1 400px',
        minWidth: 0,
    },
}));

export const SelfServeDashboard = ({
    toggleColumn,
    onSortChange,
    selectSortBy,
    selectHiddenColumns,
    currentList,
    currentConfig,
    activeFilter,
    isSelfServeRoute,
    handleChangeActiveFilter,
}) => {
    const classes = useStyles();

    const {
        init,
        fetchUserPreference,
        fetchSelfServeOrgList,
        isLoading,
        hasLoadedUserPreference,
        organizationFilter,
        updateOrganizationFilter,
        selfServeOrgList,
    } = useOverviewAdData();
    const isDashboardLoading = isLoading || !hasLoadedUserPreference;

    const tableRef = useRef();
    const [data, setData] = useState({
        listData: currentList,
        selfServeOptions: [],
    });
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [importText, setImportText] = useState('');
    const [draftOrganizationFilter, setDraftOrganizationFilter] = useState([]);
    const [appliedOrganizationFilter, setAppliedOrganizationFilter] = useState([]);
    const hasInitializedDashboard = useRef(false);

    /** Accent-insensitive / org-id filter on the original MultiSelect search input. */
    const organizationSelectFilterOptions = useCallback(
        (options, state) =>
            filterRecordsByKeywords(options, state.inputValue, {
                nameField: 'label',
                idField: 'value',
            }),
        []
    );

    useEffect(() => {
        fetchUserPreference();

        // Don't re-request if list is already fetched.
        if (!(selfServeOrgList && selfServeOrgList.length)) {
            fetchSelfServeOrgList();
        }
    }, []);

    useEffect(() => {
        if (!hasLoadedUserPreference || hasInitializedDashboard.current) {
            return;
        }

        const savedOrganizationIds = organizationFilter || [];
        hasInitializedDashboard.current = true;
        setDraftOrganizationFilter(savedOrganizationIds);
        setAppliedOrganizationFilter(savedOrganizationIds);
        init({
            showAllSS: true,
            organizationIds: savedOrganizationIds,
        });
    }, [hasLoadedUserPreference, organizationFilter, init]);

    useEffect(() => {
        setDraftOrganizationFilter(organizationFilter || []);
    }, [organizationFilter]);

    const applyOrganizationFilter = organizationIds => {
        setAppliedOrganizationFilter(organizationIds);
        updateOrganizationFilter({
            organizationFilter: organizationIds,
        });
        init({
            showAllSS: true,
            organizationIds,
        });
    };

    // Apply the shared organization filter to the active tab's rows.
    useEffect(() => {
        const listData =
            appliedOrganizationFilter.length > 0
                ? currentList.filter(({ organization }) =>
                      includes(appliedOrganizationFilter, organization)
                  )
                : currentList;

        setData(prev => ({
            ...prev,
            listData,
        }));
    }, [currentList, appliedOrganizationFilter, activeFilter]);

    // Locale-aware sort for the organization MultiSelect dropdown (filter runs on Autocomplete input).
    useEffect(() => {
        const organizationOptions = (selfServeOrgList || []).map(({ id, name }) => ({
            label: name,
            value: id,
        }));

        setData(prev => ({
            ...prev,
            selfServeOptions: sortRecordsByName(organizationOptions),
        }));
    }, [selfServeOrgList]);

    const handleImportOrganizations = () => {
        const knownOrganizationIds = new Set((selfServeOrgList || []).map(({ id }) => String(id)));
        const importedIds = [
            ...new Set(
                importText
                    .split(/[\s,;]+/)
                    .map(id => id.trim())
                    .filter(id => id && knownOrganizationIds.has(id))
            ),
        ];

        setDraftOrganizationFilter(importedIds);
        applyOrganizationFilter(importedIds);

        setImportText('');
        setIsImportOpen(false);
    };

    const closeImportOrganizations = () => {
        setImportText('');
        setIsImportOpen(false);
    };

    const hasUnappliedOrganizationFilter =
        draftOrganizationFilter.length !== appliedOrganizationFilter.length ||
        draftOrganizationFilter.some(id => !appliedOrganizationFilter.includes(id));

    const calculateMinHeight = () => {
        if (isDashboardLoading || !tableRef.current) {
            return 0;
        }

        // We calculate the distance between the table container to the bottom of the window.
        // This is so we can show as much ads/campaigns as possible in the window.
        // If there are a small number of ads/campaigns then we'll just calculate
        // the height based on that.
        return currentList.length
            ? Math.min(
                  window.innerHeight - tableRef.current.getBoundingClientRect().top - 70,
                  currentList.length * 87 + 40
              )
            : 400;
    };

    return (
        <React.Fragment>
            <Grid container flexDirection="column">
                <Grid item xs>
                    <Box pt="7">
                        <Typography variant="subtitle2">Filter by Organization:</Typography>
                        <Box display="flex" alignItems="center" flexWrap="wrap" mb="10">
                            <Box className={classes.orgFilterBox}>
                                <MultiSelect
                                    value={draftOrganizationFilter}
                                    onChange={organizationIds => {
                                        setDraftOrganizationFilter(organizationIds);
                                        updateOrganizationFilter({
                                            organizationFilter: organizationIds,
                                        });
                                    }}
                                    options={data.selfServeOptions}
                                    filterOptions={organizationSelectFilterOptions}
                                    placeholder="Search Organizations by Name or ID, or leave empty for all"
                                    formFullWidth
                                    fullWidth
                                    formControlClass={classes.formControl}
                                />
                            </Box>
                            <Box display="flex" alignItems="center" flexShrink={0} ml={1}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    disabled={!selfServeOrgList?.length}
                                    onClick={() => setIsImportOpen(true)}
                                    style={{ marginRight: 8 }}
                                >
                                    Import IDs
                                </Button>
                                <Tooltip title="Click when enabled to load the table using filters">
                                    <span>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            disabled={
                                                isDashboardLoading ||
                                                !hasUnappliedOrganizationFilter
                                            }
                                            onClick={() =>
                                                applyOrganizationFilter(draftOrganizationFilter)
                                            }
                                        >
                                            Apply Filters
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
                {isDashboardLoading ? <LinearProgress /> : null}
            </Grid>
            <Dialog open={isImportOpen} onClose={closeImportOrganizations} maxWidth="sm" fullWidth>
                <DialogTitle>Import Organizations</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        multiline
                        minRows={8}
                        fullWidth
                        variant="outlined"
                        label="Organization IDs"
                        placeholder="Paste one organization ID per line, or separate IDs with commas"
                        value={importText}
                        onChange={event => setImportText(event.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeImportOrganizations} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleImportOrganizations}
                        color="primary"
                        variant="contained"
                        disabled={isDashboardLoading || !importText.trim()}
                    >
                        Replace Filters
                    </Button>
                </DialogActions>
            </Dialog>
            {!isDashboardLoading ? (
                <Tabs
                    activeFilter={activeFilter}
                    handleChangeActiveFilter={handleChangeActiveFilter}
                />
            ) : null}
            <Grid xs="12" ref={tableRef}>
                {!isDashboardLoading ? (
                    <VirtualizedTable
                        data={data.listData}
                        minHeight={calculateMinHeight()}
                        itemHeight={87}
                        isLoading={false}
                        noResultsText="No Ads or Campaigns found."
                        canToggleColumns
                        toggleColumn={column => toggleColumn(column)}
                        onSortChange={onSortChange}
                        columns={currentConfig.columns}
                        hiddenColumns={selectHiddenColumns()}
                        sortBy={selectSortBy()}
                        canExport
                        exportFilename={`dashboard-${moment(new Date()).format(
                            'YYYY-MM-DD'
                        )}-${activeFilter}.csv`}
                        showConditions={{ isSelfServeRoute }}
                    />
                ) : null}
            </Grid>
        </React.Fragment>
    );
};
